import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, Star, ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader } from "@/components/Loader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Bubble({ m, i }) {
  const mine = m.role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${mine ?"justify-end" : "justify-start"}`}
      data-testid={`coach-msg-${m.role}-${i}`}
    >
      <div
        className={`a2g-read max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-base leading-relaxed ${ mine ?"rounded-br-md bg-gradient-to-br from-[#E5C158] to-[#B8941F] text-[#09090B]"
            : "rounded-bl-md border border-[#27272A] bg-[#121215] text-[#E4E4E7]"
        }`}
      >
        {m.content.replace(/\*\*/g, "") || <span className="animate-pulse text-[#8A8E96]">Coach Mino sta scrivendo…</span>}
      </div>
    </motion.div>
  );
}

function Rating({ m, onRate, isAdmin }) {
  if (m.role !== "assistant" || !m.id) return null;
  const btn = (v, Icon, label) => (
    <button onClick={() => onRate(m.id, m.rating === v ? 0 : v)} aria-label={label} data-testid={`coach-rate-${v > 0 ? "up" : "down"}-${m.id}`}
      className={`rounded-full p-1.5 transition-colors ${m.rating === v ? "bg-[#D4AF37]/20 text-[#E5C158]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}>
      <Icon size={15} />
    </button>
  );
  const saveExample = () => api.post(`/admin/coach-examples/${m.id}`).then(() => toast.success("Salvata come risposta modello")).catch(() => toast.error("Salvataggio non riuscito"));
  return (
    <div className="mt-1 flex items-center gap-1 pl-1">
      {btn(1, ThumbsUp, "Risposta utile")}{btn(-1, ThumbsDown, "Risposta non utile")}
      {isAdmin && (
        <button onClick={saveExample} data-testid={`coach-save-example-${m.id}`} className="ml-1 flex items-center gap-1 rounded-full px-2 py-1 text-[12px] text-[#A1A1AA] hover:text-[#E5C158]">
          <Star size={13} /> Salva come modello
        </button>
      )}
    </div>
  );
}

async function streamReply(text, onDelta, onReplace) {
  const res = await fetch(`${BACKEND_URL}/api/coach/message`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Qualcosa è andato storto. Riprova.");
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let last = {};
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split("\n\n");
    buf = lines.pop();
    for (const l of lines) {
      if (!l.startsWith("data: ")) continue;
      const d = JSON.parse(l.slice(6));
      if (d.delta) onDelta(d.delta);
      if (d.replace) onReplace(d.replace);
      if (d.error) throw new Error(d.error);
      last = d;
    }
  }
  return last;
}

export default function Coach() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/coach").then(({ data }) => setData(data)).catch((e) => {
      setError(apiError(e?.response?.data?.detail));
      setData({ messages: [], remaining: 0, limit: 60 });
    });
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages]);

  if (!data) return <Loader />;
  const { messages, remaining, month_remaining: monthLeft, limit, enabled = true } = data;
  const blocked = remaining === 0 || !enabled;
  const lastReply = busy ? -1 : messages.map((m) => m.role).lastIndexOf("assistant");
  const status = !enabled ? data.disabled_message : remaining == null ? "Messaggi illimitati (admin)" : blocked && error ? "Coach non disponibile"
    : blocked ? data.limit_message || "Hai usato i messaggi disponibili." : `${monthLeft} di ${limit} messaggi rimasti questo mese`;

  const rate = (id, rating) => {
    setData((d) => ({ ...d, messages: d.messages.map((m) => (m.id === id ? { ...m, rating } : m)) }));
    api.post("/coach/feedback", { id, rating }).catch(() => {});
  };

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || busy || blocked) return;
    setBusy(true);
    setError("");
    setText("");
    setData((d) => ({ ...d, messages: [...d.messages, { role: "user", content: t }, { role: "assistant", content: "" }] }));
    const append = (delta) =>
      setData((d) => {
        const msgs = [...d.messages];
        msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], content: msgs[msgs.length - 1].content + delta };
        return { ...d, messages: msgs };
      });
    try {
      const replace = (content) =>
        setData((d) => {
          const msgs = [...d.messages];
          msgs[msgs.length - 1] = { role: "assistant", content };
          return { ...d, messages: msgs };
        });
      const last = await streamReply(t, append, replace);
      setData((d) => ({ ...d, messages: d.messages.map((m, k) => (k === d.messages.length - 1 && last.id ? { ...m, id: last.id } : m)),
        remaining: last.remaining ?? d.remaining, month_remaining: last.month_remaining ?? d.month_remaining, reason: last.reason,
        limit_message: last.reason === "month" ? `Hai usato i ${d.limit} messaggi di questo mese. Si ricaricano il ${d.resets_on}.`
          : last.reason === "day" ? `Hai raggiunto il massimo di ${d.daily_cap} messaggi per oggi. Coach Mino ti aspetta domani!` : null }));
    } catch (err) {
      setError(err.message);
      setData((d) => ({ ...d, messages: d.messages.slice(0, -2) }));
      setText(t);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title="Coach Mino" greeting="Il tuo coach personale">
      <div className="flex flex-col px-5 pt-5">
        {messages.length === 0 && (
          <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#121215] p-5" data-testid="coach-empty">
            <Sparkles size={22} className="text-[#D4AF37]" />
            <p className="a2g-read mt-3 text-base leading-relaxed text-[#D4D4D8]">
              Ciao, sono Coach Mino. Conosco il tuo percorso e il punto in cui sei. Raccontami cosa ti blocca oggi o chiedimi come applicare l'azione del giorno.
            </p>
          </div>
        )}
        <div className="mt-4 space-y-3" data-testid="coach-messages">
          {messages.map((m, i) => (
            <div key={i}>
              <Bubble m={m} i={i} />
              <Rating m={m} onRate={rate} isAdmin={user?.role === "admin"} />
              {i === lastReply && monthLeft != null && (
                <p className="mt-1.5 pl-1 text-[13px] text-[#8A8E96]" data-testid="coach-credits-after-reply">
                  {monthLeft === 1 ? "Ti resta 1 messaggio questo mese" : `Ti restano ${monthLeft} messaggi questo mese`}
                </p>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        {error && <p className="mt-3 text-sm text-[#F87171]" data-testid="coach-error">{error}</p>}
      </div>
      <form
        onSubmit={send}
        className="fixed bottom-[68px] left-1/2 z-40 w-full max-w-[430px] -translate-x-1/2 border-t border-[#27272A]/60 bg-[#09090B]/95 px-4 pb-3 pt-2 backdrop-blur-xl"
      >
        <p className="mb-2 text-center text-[12px] text-[#8A8E96]" data-testid="coach-remaining">
          {status}
        </p>
        <div className="flex items-end gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send(e)}
            rows={1}
            maxLength={1000}
            disabled={blocked || busy}
            placeholder={!enabled ? "Coach in pausa" : blocked ? (data.reason === "month" ? `Torna il ${data.resets_on}` : "Torna domani") : "Scrivi a Coach Mino…"}
            data-testid="coach-input"
            className="max-h-32 flex-1 resize-none rounded-2xl border border-[#27272A] bg-[#121215] px-4 py-3 text-base text-[#FAF9F6] placeholder:text-[#8A8E96] focus:border-[#D4AF37]/60 focus:outline-none disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!text.trim() || busy || blocked}
            data-testid="coach-send-button"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#E5C158] to-[#997A20] text-[#09090B] transition-transform active:scale-95 disabled:opacity-40"
          >
            <Send size={18} />
          </button>
        </div>
      </form>
      <div className="h-28" />
    </AppShell>
  );
}
